/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../services/api";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LocationSetup from "../components/LocationSetup";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  marginBottom: theme.spacing(2),
}));

interface ProfileData {
  name: string;
  email: string;
  phoneNumber: string;
  company: string;
  bio: string;
  location: string;
  timezone: string;
  avatar: string;
}

export const Profile: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState<ProfileData>({
    name: "",
    email: "",
    phoneNumber: "",
    company: "",
    bio: "",
    location: "",
    timezone: "",
    avatar: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        setError("User not logged in or ID not available.");
        return;
      }
      try {
        const response = await api.get("/api/profile");
        setProfile(response.data);
      } catch (err) {
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put("/api/profile", profile);
      setSuccess("Profile updated successfully");
    } catch (err) {
      setError("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom>
        Profile Settings
      </Typography>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Profile" />
        <Tab label="Location" />
      </Tabs>

      {activeTab === 0 && (
        <StyledPaper>
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <StyledAvatar src={profile.avatar} alt={profile.name} />
            <Typography variant="h6">{profile.name}</Typography>
            <Typography color="textSecondary">{profile.email}</Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box flex="1" minWidth="250px">
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    required
                  />
                </Box>
                <Box flex="1" minWidth="250px">
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={profile.phoneNumber}
                    onChange={handleChange}
                  />
                </Box>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="Company"
                  name="company"
                  value={profile.company}
                  onChange={handleChange}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="Bio"
                  name="bio"
                  value={profile.bio}
                  onChange={handleChange}
                  multiline
                  rows={4}
                />
              </Box>

              <Box display="flex" gap={3} flexWrap="wrap">
                <Box flex="1" minWidth="250px">
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={profile.location}
                    onChange={handleChange}
                  />
                </Box>
                <Box flex="1" minWidth="250px">
                  <TextField
                    fullWidth
                    label="Timezone"
                    name="timezone"
                    value={profile.timezone}
                    onChange={handleChange}
                  />
                </Box>
              </Box>

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : "Save Profile"}
                </Button>
              </Box>
            </Box>
          </form>
        </StyledPaper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <LocationSetup />
        </Paper>
      )}
    </Container>
  );
};